<template>
  <div class="card mb-2">
    <div class="card-header bg-primary text-white d-inline-flex">
      <a v-b-toggle="`collapse-view-property-card`" class="collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
      <span class="font-weight-bold">Property Details</span>
      <span  class="text-white ml-auto c-pointer">
        <span
          v-if="$store.getters['User/isCurrentUserAdmin'] && canViewDeleteButton()"
          @click="deleteConfirmationModal"
        >
          Delete |
        </span>
        <span
          v-if="!$store.getters['User/isCurrentUserViewOnly']"
          @click="editPropertyDetails"
        >
          Edit
        </span>
      </span>

    </div>
    <b-collapse
      :id="`collapse-view-property-card`"
      v-model="showCollapse"
    >
      <div class="card-body">
        <LoadingSpinner v-if="propertiesLoading" message ='Loading Property Details' size = '1x'>
        </LoadingSpinner>
        <template v-if="!propertiesLoading">
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Property ID
            </div>
            <div class="col-sm-8">
              {{ currentProperty.id }}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Property Name
            </div>
            <div class="col-sm-8">
              {{ getPropertyName() }}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Property Status
            </div>
            <div class="col-sm-8">
              <font-awesome-icon
                :id="'prop-' + currentProperty.id"
                icon="circle"
                class="rounded-circle border border-white border-circle"
                size="sm"
                :class="getStatusClass(currentProperty.statusId, 'Property')" />
              {{getPropertyStatusName(currentProperty.statusId)}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Calculated Acres
            </div>
            <div class="col-sm-8">
              {{currentProperty.calculatedAcres | acresFormatNullIsNA}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Divisions Allowed
            </div>
            <div class="col-sm-8"
              v-bind:class=
                "{ 'text-danger':
                  isNegativeNumber(currentProperty.futurePropertiesAllowed) }">
              {{currentProperty.futurePropertiesAllowed | numberFormatNullIsNA}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Target Inspection Frequency (Years)
            </div>
            <div class="col-sm-8">
              {{ getTargetInspectionString() }}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Primary Access Point<br>(Latitude, Longitude)
            </div>
            <div class="col-sm-8">
              {{currentProperty.accessPointLatitude ?
                  currentProperty.accessPointLatitude : 'None'}},
              {{currentProperty.accessPointLongitude ?
                  currentProperty.accessPointLongitude : 'None'}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Inspect Property with Two or More Staff
            </div>
            <div class="col-sm-8">
                {{currentProperty.inspectWithTwoOrMoreStaff | formatBooleanAsYesNo}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Additional Interest Holder
            </div>
            <div class="col-sm-8">
                {{currentProperty.additionalInterestHolder | formatBooleanAsYesNo}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Property Notes
            </div>
            <div class="col-sm-8 notes-field">{{ getPropertyNotes() }}</div>
          </div>
          <div
            v-if="currentProperty.legacyPropertyIds && currentProperty.legacyPropertyIds !== ''"
            class="form-group row">
            <div class="col-sm-4 label-text">
              Legacy Property ID(s)
            </div>
            <div class="col-sm-8">
              {{currentProperty.legacyPropertyIds}}
            </div>
          </div>
        </template>
      </div>
    </b-collapse>
    <b-modal ref="deleteConfirmation" :id="`${currentProperty.id}`" :title="`Delete Property ${currentProperty.id}`">
      Are you sure you want to delete this Property? All associated Tax Parcels, Built Environment Features, and links to Contacts will be deleted.
      <div slot="modal-footer">
        <b-button variant="danger" class="button-row-spacing" @click="hideModal">
          Cancel
        </b-button>
        <b-button @click="onDeleteConfirmed()">
          Delete
        </b-button>
      </div>
    </b-modal>
    <b-modal ref="deleteBlockerModal" :id="`blocker-${currentProperty.id}`" :title="`Cannot Delete Property ${currentProperty.id}`">
      {{ deleteBlockMessage }}
      <div slot="modal-footer">
        <b-button @click="hideDeleteBlockerModal">
          Okay
        </b-button>
      </div>
    </b-modal>
    <b-modal ref="deleteSuccessModal" :id="`deleteSuccess-${currentProperty.id}`" :title="`Successfully Deleted Property ${currentProperty.id}`">
      Folders in Box must be deleted manually. GIS polygon must be independently removed.
      <div slot="modal-footer">
        <b-button @click="hideDeleteSuccessModal">
          Okay
        </b-button>
      </div>
    </b-modal>
  </div>
</template>


<script>
import { mapState, mapGetters } from 'vuex';
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import LoadingSpinner from '../LoadingSpinner.vue';
import CollapseControl from '../../mixins/CollapseControl';
import StatusHelper from '../../mixins/StatusHelper';

export default {
  name: 'ViewProperty',
  mixins: [CollapseControl, StatusHelper],
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      showCollapse: true,
      deleteBlockMessage: null,
    };
  },
  computed: {
    ...mapState({
      currentProperty: state => state.Property.currentProperty,
      propertiesLoading: state => state.Property.propertiesLoading,
    }),
    ...mapGetters(LookupModules.PROPERTY_STATUSES.namespace, {
      getPropertyStatusName: 'getName',
      propertyStatusOptions: 'getActiveOptions',
    }),
    ...mapGetters('Property', ['propertyCount']),
    associatedServices() {
      return this.$store.state[EntityModules.ASSOCIATED_SERVICES.namespace].entities;
    },
  },
  created() {
    this.$store.dispatch(`${LookupModules.PROPERTY_STATUSES.namespace}/fetchOptions`,
      LookupModules.PROPERTY_STATUSES.url);
  },
  methods: {
    editPropertyDetails() {
      this.$store.dispatch('Property/switchToEdit');
      this.$store.dispatch('Common/startEditMode', 'Property');
    },
    getPropertyName() {
      if (!this.currentProperty.name || this.currentProperty.name === '') {
        return 'None';
      }
      return this.currentProperty.name;
    },
    getPropertyNotes() {
      if (!this.currentProperty.notes || this.currentProperty.notes === '') {
        return 'None';
      }
      return this.currentProperty.notes;
    },
    getTargetInspectionString() {
      if (this.currentProperty.targetInspectionFrequency) {
        return this.currentProperty.targetInspectionFrequency;
      }
      return 'None';
    },
    isNegativeNumber(amount) {
      if (amount) return (Number(amount) < 0);
      return false;
    },
    canViewDeleteButton() {
      if (this.propertyCount <= 1) return false;
      if (this.currentProperty.statusId !== 2) return false;
      return true;
    },
    deleteConfirmationModal() {
      // if project.properties <= 1 then can not delete
      if (this.propertyCount <= 1) {
        this.deleteBlockMessage = 'Project must have at least one property.  Can not delete the last property in the project.';
        this.$refs.deleteBlockerModal.show();
        return;
      }

      // if property.status !== inactive then can not delete
      if (this.currentProperty.statusId !== 2) {
        this.deleteBlockMessage = 'Property status must be set to inactive before it is deleted.';
        this.$refs.deleteBlockerModal.show();
        return;
      }

      const dependantInstruments = [];
      const activeInstruments = [];
      this.currentProperty.propertyInstruments.forEach((propertyInstrument) => {
        if (propertyInstrument.instrument && propertyInstrument.instrument.associatedPropertyIds.length === 1) {
          dependantInstruments.push(propertyInstrument.instrument.id);
        }
        if (propertyInstrument.instrument.statusId !== 3) {
          activeInstruments.push(propertyInstrument.instrument.id);
        }
      });


      if (activeInstruments.length > 0) {
        this.deleteBlockMessage = 'Related Active or Pending Instrument must first be marked Inactive or disassociated from this Property.';
        this.$refs.deleteBlockerModal.show();
        return;
      }

      // if property.propertyInstruments.any(x => x.instrument.propertyCount === 1) then can not delete.
      if (dependantInstruments.length > 0) {
        this.deleteBlockMessage = 'Related Instrument must first be deleted or associated to another Property.';
        this.$refs.deleteBlockerModal.show();
        return;
      }

      if (this.associatedServices && this.associatedServices.length > 0) {
        this.deleteBlockMessage = 'Related Service must first be deleted or associated to another Property.';
        this.$refs.deleteBlockerModal.show();
        return;
      }
      // validation to ensure instruments always have a property

      this.$refs.deleteConfirmation.show();
    },
    onDeleteConfirmed() {
      this.hideModal();
      this.$store.dispatch('Property/deleteProperty', { propertyId: this.currentProperty.id, submittedCallback: this.showDeleteSuccessModal });
    },
    hideModal() {
      this.$refs.deleteConfirmation.hide();
    },
    hideDeleteBlockerModal() {
      this.deleteBlockMessage = null;
      this.$refs.deleteBlockerModal.hide();
    },
    showDeleteBlockerModal() {
      this.$refs.deleteBlockerModal.show();
    },
    showDeleteSuccessModal() {
      this.$refs.deleteSuccessModal.show();
    },
    hideDeleteSuccessModal() {
      this.$refs.deleteSuccessModal.hide();
      this.$store.dispatch('Property/forceFetchProperties', this.$store.state.CurrentProject.currentProject.id)
        .then(() => {
          this.$router.push({
            name: 'projectProperties',
            params: { project_id: parseInt(this.$route.params.project_id, 10) },
          });
        });
    },
  },
};
</script>
